import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';
import {
  clockOffset,
  deserializeTimer,
  isNewer,
  timerActions,
} from './serverTimer.js';

const CHANNEL = 'bdtt-timer';
const POLL_MS = 60 * 1000;

function openChannel() {
  try {
    return typeof BroadcastChannel === 'function'
      ? new BroadcastChannel(CHANNEL)
      : null;
  } catch {
    return null;
  }
}

// The user's timer as the server holds it. Seeded from /bootstrap, then kept
// current by its own actions, by the other tabs of this browser (a
// BroadcastChannel carries every change instantly) and by a refetch when the
// tab regains focus or every minute while it's visible (other devices, MCP).
//
// Every timer response — also a 409 refusal — carries { timer, serverNow,
// limits }; adopting it is how a stale tab catches up instead of erroring.
export function useServerTimer(initial) {
  const offsetRef = useRef(clockOffset(initial?.serverNow));
  const lastAppliedRef = useRef(initial?.serverNow || null);
  const [timer, setTimer] = useState(() =>
    deserializeTimer(initial?.timer, offsetRef.current),
  );
  const [limits, setLimits] = useState(initial?.limits || null);
  const channelRef = useRef(null);
  // The timer as rendered — the one a click acts on (see timerActions).
  const timerRef = useRef(timer);
  timerRef.current = timer;

  const apply = useCallback((payload) => {
    if (!payload || !('timer' in payload) || !payload.serverNow) return false;
    if (!isNewer(payload.serverNow, lastAppliedRef.current)) return false;
    lastAppliedRef.current = payload.serverNow;
    offsetRef.current = clockOffset(payload.serverNow);
    setTimer(deserializeTimer(payload.timer, offsetRef.current));
    if (payload.limits) setLimits(payload.limits);
    return true;
  }, []);

  const refresh = useCallback(async () => {
    try {
      apply(await api('/timer'));
    } catch {
      // A failed background refresh keeps the last known timer; the next
      // focus or poll tries again.
    }
  }, [apply]);

  useEffect(() => {
    const channel = openChannel();
    channelRef.current = channel;
    if (channel) channel.onmessage = (event) => apply(event.data);
    return () => {
      channelRef.current = null;
      channel?.close();
    };
  }, [apply]);

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') refresh();
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      window.clearInterval(id);
    };
  }, [refresh]);

  // Run one timer request; adopt and broadcast whatever timer it answers
  // with. A 409 (the timer changed elsewhere) is not an error — the adopted
  // state is the answer. Anything else is rethrown for the bar to show.
  const send = useCallback(
    async (path, method, body) => {
      try {
        const payload = await api(path, {
          method,
          body: body ? JSON.stringify(body) : undefined,
        });
        if (apply(payload)) channelRef.current?.postMessage(payload);
        return payload;
      } catch (error) {
        if (error.data?.serverNow && apply(error.data)) {
          channelRef.current?.postMessage({
            timer: error.data.timer,
            serverNow: error.data.serverNow,
            limits: error.data.limits,
          });
        }
        if (error.status === 409) return null;
        throw error;
      }
    },
    [apply],
  );

  const actions = useMemo(
    () => timerActions(send, () => timerRef.current?.id),
    [send],
  );

  return { timer, limits, actions, refresh };
}
